/**
 * Profile media tab folder migration — pure planning logic + constants.
 * Used by Admin (Migrations) and documented for one-off scripts.
 */

export const PROFILE_TAB_FOLDER_NAMES = {
  SAVED_VENUES: 'My Saved Venues',
  TOURS: 'My Tours',
  COMMUNITY: 'My Community Posts',
  GENERAL: 'My Media',
  /** Tab root for profile "My Room Notes" — room notes + Room EQ submission shortcuts. */
  ROOM_NOTES: 'My Room Notes',
  /** Venue report cards (`itemType: report`) from venue_reports / library. */
  REPORTS: 'My Reports',
};

/** Bump when `planProfileTabMigration` behavior changes (Admin / bulk re-run). */
export const PROFILE_TAB_MIGRATION_VERSION = 10;

/** Library file row for a venue Room EQ submission (Firestore: venues/{venueId}/roomEq/{id}). */
export const ITEM_TYPE_ROOM_EQ_SUBMISSION = 'roomEqSubmission';

function isProtectedUserUploads(folder) {
  return folder && folder.protected === true && folder.name === 'User Uploads';
}

function cloneRecordMap(map) {
  const out = {};
  for (const k of Object.keys(map || {})) {
    const v = map[k];
    out[k] = v && typeof v === 'object' ? { ...v } : v;
  }
  return out;
}

function isTabRootFolder(folder) {
  return !!(folder && folder.tabRootSlug);
}

/** Pre-migration tree: folder has Tour Planner `tour` payload or contains such a folder. */
function origFolderHasTourInSubtree(folderId, origFolders) {
  const f = origFolders[folderId];
  if (!f) return false;
  if (f.tour) return true;
  for (const [cid, child] of Object.entries(origFolders)) {
    if (child && child.parentId === folderId && origFolderHasTourInSubtree(cid, origFolders)) return true;
  }
  return false;
}

function origSavedVenueLivesUnderTourContext(file, origFolders) {
  if (!file || file.itemType !== 'savedVenue') return false;
  const pid = file.parentId;
  if (pid == null || pid === undefined) return false;
  return origFolderHasTourInSubtree(pid, origFolders);
}

/**
 * @param {string} userId
 * @param {Record<string, object>} folders
 * @param {Record<string, object>} files
 * @param {object} [options]
 * @param {Record<string, string>|null} [options.profileMedia] — `users/{uid}.media` filename → URL; each becomes a My Media file (deterministic ids).
 * @param {Array<{ venueId: string, id: string, venueName?: string }>|null} [options.roomEqSubmissions] — from each venue's roomEq subcollection scan; library shortcuts under My Room Notes.
 * @returns {{ updatedFolders: Record<string,object>, updatedFiles: Record<string,object>, profileTabRoots: object, moves: string[] }}
 */
export function planProfileTabMigration(userId, folders = {}, files = {}, options = {}) {
  const { profileMedia = null, roomEqSubmissions = null } = options;

  const moves = [];
  const origFolders = cloneRecordMap(folders);
  const origFiles = cloneRecordMap(files);
  const updatedFolders = cloneRecordMap(folders);
  const updatedFiles = cloneRecordMap(files);

  const mirrorCache = new Map();

  const existingRootOrder = () =>
    Math.max(
      0,
      ...Object.values(updatedFolders)
        .filter((f) => f && (f.parentId === null || f.parentId === undefined))
        .map((f) => (typeof f.order === 'number' ? f.order : 0))
    ) + 1;

  const ensureRoot = (slug, displayName) => {
    const existing = Object.entries(updatedFolders).find(
      ([, f]) => f && f.tabRootSlug === slug && (f.parentId === null || f.parentId === undefined)
    );
    if (existing) return existing[0];
    const id = `folder_tab_${slug}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    updatedFolders[id] = {
      id,
      name: displayName,
      parentId: null,
      order: existingRootOrder(),
      color: '#3D7FE6',
      tabRootSlug: slug,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    moves.push(`Created tab root folder "${displayName}" (${id})`);
    return id;
  };

  const idSaved = ensureRoot('saved_venues', PROFILE_TAB_FOLDER_NAMES.SAVED_VENUES);
  const idTours = ensureRoot('tours', PROFILE_TAB_FOLDER_NAMES.TOURS);
  const idCommunity = ensureRoot('community', PROFILE_TAB_FOLDER_NAMES.COMMUNITY);
  const idGeneral = ensureRoot('general', PROFILE_TAB_FOLDER_NAMES.GENERAL);
  const idRoomNotes = ensureRoot('room_notes', PROFILE_TAB_FOLDER_NAMES.ROOM_NOTES);
  const idReports = ensureRoot('reports', PROFILE_TAB_FOLDER_NAMES.REPORTS);

  const tabRootFolderIds = new Set([idSaved, idTours, idCommunity, idGeneral, idRoomNotes, idReports]);

  /**
   * Mirror an original folder chain under a tab root. Tab roots in the ancestor path collapse to `tabRootId`.
   * @param {string|null|undefined} origFolderId
   * @param {'tours'|'general'|'community'|'saved_venues'|'room_notes'|'reports'} tabKey
   * @param {string} tabRootId
   * @param {boolean} preserveTourFlag — only for `tours` tab mirrors
   */
  /** Short prefix so folder ids stay unambiguous (avoids `saved_venues_*` underscore collisions). */
  const mirrorKey = (tabKey) =>
    ({
      tours: 't',
      general: 'g',
      community: 'c',
      saved_venues: 's',
      room_notes: 'r',
      reports: 'p',
    }[tabKey] || 'x');

  const ensureMirroredFolder = (origFolderId, tabKey, tabRootId, preserveTourFlag) => {
    if (origFolderId == null || origFolderId === undefined) return tabRootId;
    if (tabRootFolderIds.has(origFolderId)) return tabRootId;

    const cacheKey = `${tabKey}:${origFolderId}`;
    if (mirrorCache.has(cacheKey)) return mirrorCache.get(cacheKey);

    const f = origFolders[origFolderId];
    if (!f) {
      moves.push(`Mirror skipped: missing folder ${origFolderId} (${tabKey})`);
      return tabRootId;
    }

    let parentMirror = tabRootId;
    const p = f.parentId;
    if (p != null && p !== undefined && !tabRootFolderIds.has(p)) {
      parentMirror = ensureMirroredFolder(p, tabKey, tabRootId, preserveTourFlag);
    }

    const newId = `folder_mir_${mirrorKey(tabKey)}_${origFolderId}`;
    const now = new Date().toISOString();
    if (!updatedFolders[newId]) {
      /** Tour Planner stores `tour` as an object, not boolean — preserve for Tours tab mirrors. */
      const row = {
        id: newId,
        name: f.name,
        parentId: parentMirror,
        order: f.order ?? 0,
        color: f.color || '#3D7FE6',
        createdAt: f.createdAt || now,
        updatedAt: now,
      };
      if (preserveTourFlag && f.tour) {
        row.tour = f.tour;
      } else if (f.tour) {
        row.sourceTourFolder = true;
      }
      updatedFolders[newId] = row;
      moves.push(`Mirrored folder "${f.name}" under ${tabKey} (${newId})`);
    }
    mirrorCache.set(cacheKey, newId);
    return newId;
  };

  const tourFolderIds = new Set(
    Object.entries(origFolders)
      .filter(([, f]) => f && f.tour)
      .map(([id]) => id)
  );

  // Legacy roomEqReference → roomNotes
  for (const fid of Object.keys(updatedFiles)) {
    const file = updatedFiles[fid];
    if (file?.itemType === 'roomEqReference') {
      updatedFiles[fid] = {
        ...file,
        itemType: 'roomNotes',
        data: { ...(file.data || {}), fileType: 'roomNotes' },
        updatedAt: new Date().toISOString(),
      };
      moves.push(`Converted legacy roomEqReference ${fid} → roomNotes`);
    }
  }

  // userData.media → My Media root (deterministic file ids)
  if (profileMedia && typeof profileMedia === 'object') {
    let slot = 0;
    for (const [key, url] of Object.entries(profileMedia)) {
      if (url == null || typeof url !== 'string' || !url.trim()) continue;
      const safeKey = String(key).replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 80);
      const fileId = `file_profile_media_${userId}_${safeKey}`;
      if (updatedFiles[fileId]) continue;
      updatedFiles[fileId] = {
        id: fileId,
        name: safeKey || 'Profile media',
        parentId: idGeneral,
        itemType: 'file',
        order: slot++,
        data: { url: url.trim(), fileType: 'image' },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      moves.push(`Imported profile media slot "${key}" → My Media (${fileId})`);
    }
  }

  // Room EQ submissions (from external scan) → My Room Notes root
  if (Array.isArray(roomEqSubmissions)) {
    let ord = 0;
    for (const sub of roomEqSubmissions) {
      if (!sub?.venueId || !sub?.id) continue;
      const fileId = `file_room_eq_sub_${sub.venueId}_${sub.id}`;
      if (updatedFiles[fileId]) continue;
      const venueName = sub.venueName ? String(sub.venueName) : 'Venue';
      updatedFiles[fileId] = {
        id: fileId,
        name: `${venueName} room info`,
        parentId: idRoomNotes,
        itemType: ITEM_TYPE_ROOM_EQ_SUBMISSION,
        order: ord++,
        data: {
          fileType: ITEM_TYPE_ROOM_EQ_SUBMISSION,
          venueId: sub.venueId,
          submissionId: sub.id,
          venueName: sub.venueName || null,
          previewImageUrl: sub.previewImageUrl || null,
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      moves.push(`Added Room EQ submission shortcut ${sub.venueId}/${sub.id} → My Room Notes (${fileId})`);
    }
  }

  // Reparent tour folders under mirrored paths in My Tours
  for (const tourFolderId of tourFolderIds) {
    const f = updatedFolders[tourFolderId];
    if (!f) continue;
    if (tourFolderId === idTours) continue;
    const orig = origFolders[tourFolderId];
    if (!orig) continue;
    const p = orig.parentId;
    let newParent = idTours;
    if (p != null && p !== undefined && !tabRootFolderIds.has(p)) {
      newParent = ensureMirroredFolder(p, 'tours', idTours, true);
    }
    updatedFolders[tourFolderId] = {
      ...f,
      parentId: newParent,
      updatedAt: new Date().toISOString(),
    };
    moves.push(`Moved tour folder "${f.name || tourFolderId}" under My Tours path (${tourFolderId} → parent ${newParent})`);
  }

  // Loose root folders (except tab roots and User Uploads) → under My Media
  for (const [folderId, folder] of Object.entries(updatedFolders)) {
    if (!folder) continue;
    if (folder.parentId != null && folder.parentId !== undefined) continue;
    if (tabRootFolderIds.has(folderId)) continue;
    if (isProtectedUserUploads(folder)) continue;
    if (isTabRootFolder(folder)) continue;
    updatedFolders[folderId] = {
      ...folder,
      parentId: idGeneral,
      updatedAt: new Date().toISOString(),
    };
    moves.push(`Moved root-level folder "${folder.name || folderId}" → under My Media tab root`);
  }

  const tabKeyForFile = (file) => {
    if (!file) return null;
    if (file.itemType === 'communityPost') return 'community';
    if (file.itemType === 'savedVenue') return 'saved_venues';
    if (file.itemType === 'roomNotes' || file.itemType === 'roomEqReference') return 'room_notes';
    if (file.itemType === ITEM_TYPE_ROOM_EQ_SUBMISSION) return 'room_notes';
    if (file.itemType === 'report') return 'reports';
    return 'general';
  };

  const rootForTabKey = (tk) => {
    if (tk === 'community') return idCommunity;
    if (tk === 'saved_venues') return idSaved;
    if (tk === 'room_notes') return idRoomNotes;
    if (tk === 'reports') return idReports;
    if (tk === 'general') return idGeneral;
    return idGeneral;
  };

  /**
   * `ensureVenueInRoomNotesFolder` adds savedVenue rows under a folder named "My Room Notes".
   * Those are replaced by roomNotes / roomEqSubmission entries on the My Room Notes tab — remove the stubs.
   */
  const isLegacyRoomNotesSavedVenueShortcut = (file, folders) => {
    if (!file || file.itemType !== 'savedVenue') return false;
    const pid = file.parentId;
    if (pid == null || pid === undefined) return false;
    const parent = folders[pid];
    if (!parent || typeof parent.name !== 'string') return false;
    return parent.name === PROFILE_TAB_FOLDER_NAMES.ROOM_NOTES;
  };

  for (const fid of Object.keys(origFiles)) {
    const f = origFiles[fid];
    if (!isLegacyRoomNotesSavedVenueShortcut(f, origFolders)) continue;
    if (updatedFiles[fid]) {
      delete updatedFiles[fid];
      moves.push(
        `Removed legacy Room EQ venue shortcut ${fid} (replaced by My Room Notes tab entries; was under "${PROFILE_TAB_FOLDER_NAMES.ROOM_NOTES}")`
      );
    }
  }

  const origFileIds = Object.keys(origFiles);

  for (const fileId of origFileIds) {
    const file = updatedFiles[fileId];
    if (!file) continue;

    const parentId = file.parentId;

    const tk = tabKeyForFile(file);
    if (tk == null) continue;

    const tabRootId = rootForTabKey(tk);
    let newParent = tabRootId;
    if (parentId != null && parentId !== undefined) {
      newParent = ensureMirroredFolder(parentId, tk, tabRootId, false);
    }

    if (updatedFiles[fileId].parentId !== newParent) {
      updatedFiles[fileId] = {
        ...updatedFiles[fileId],
        parentId: newParent,
        updatedAt: new Date().toISOString(),
      };
      moves.push(`Placed file ${fileId} (${file.itemType || 'file'}) under ${tk} mirror (parent ${newParent})`);
    }
  }

  // Saved venues under tour-related folders: duplicate row under My Tours mirror (same venue data; primary stays on Saved Venues mirror).
  for (const fileId of origFileIds) {
    const origFile = origFiles[fileId];
    if (!origFile || origFile.itemType !== 'savedVenue') continue;
    if (!origSavedVenueLivesUnderTourContext(origFile, origFolders)) continue;

    const dupId = `file_sv_toursdup_${fileId}`;
    const origParentId = origFile.parentId;
    const origParentFold = origFolders[origParentId];
    /** Tour leg folders keep their real id under My Tours; mirroring them again would create an empty duplicate name. */
    const toursDupParentId =
      origParentFold && origParentFold.tour
        ? origParentId
        : ensureMirroredFolder(origParentId, 'tours', idTours, true);
    const base = updatedFiles[fileId];
    if (!base) continue;

    updatedFiles[dupId] = {
      ...base,
      id: dupId,
      parentId: toursDupParentId,
      tourTabMirrorOf: fileId,
      updatedAt: new Date().toISOString(),
    };
    moves.push(`Duplicated saved venue ${fileId} → My Tours mirror (${dupId}, parent ${toursDupParentId})`);
  }

  const profileTabRoots = {
    savedVenuesFolderId: idSaved,
    toursFolderId: idTours,
    communityFolderId: idCommunity,
    generalFolderId: idGeneral,
    roomNotesFolderId: idRoomNotes,
    reportsFolderId: idReports,
    migrationVersion: PROFILE_TAB_MIGRATION_VERSION,
    migratedAt: new Date().toISOString(),
    migratedForUid: userId,
  };

  return { updatedFolders, updatedFiles, profileTabRoots, moves };
}

export function buildRoomNotesFileStub(venueId, submissionId, venueName) {
  return {
    itemType: 'roomNotes',
    name: `${venueName || 'Venue'} — Room notes`,
    data: {
      fileType: 'roomNotes',
      venueId,
      submissionId,
    },
  };
}

/** @deprecated Use buildRoomNotesFileStub — kept for older imports */
export function buildRoomEqReferenceFileStub(venueId, submissionId, venueName) {
  return buildRoomNotesFileStub(venueId, submissionId, venueName);
}
